import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { startEdit, finishEdit } from 'actions/matrixSettings';
import { saveMatrix } from 'actions/matrices';
import { useSelector, useDispatch } from 'react-redux';
import ConnectionsTable from 'components/connectionsView/ConnectionsTable';
import Button from 'components/inputs/Button';
import ButtonNew from 'components/inputs/ButtonNew';
import ImportConnections from './ImportConnections';
import { exportConnections, getFilename } from 'export/export_connections';
import * as urls from 'constants/urls';
import { compareMatrices } from 'constants/matrices';

import './ConnectionsAdvanced.scss';

const ConnectionsAdvanced = ({ matrix, onCancel }) => {
  const [descriptor, setDescriptor] = useState(null);
  const dispatch = useDispatch();

  const isExperiment = useMemo(() => !!matrix?.parentId, [matrix]);

  const editingMatrix = useSelector(state => state.matrixSettings.matrix);

  // Start edit every time editing matrix finishes
  useEffect(() => {
    if (editingMatrix) {
      return;
    }
    dispatch(startEdit(matrix));
  }, [dispatch, matrix, editingMatrix]);

  const handleExport = () => {
    exportConnections(matrix, getFilename(matrix));
  };

  const onConfirmEdit = useCallback(() => {
    if (!matrix || !editingMatrix) return;
    const comparison = compareMatrices(matrix, editingMatrix);
    const hasChanges = !!Object.values(comparison).flat(Infinity).length;
    if (hasChanges) {
      dispatch(finishEdit(editingMatrix));
      dispatch(saveMatrix(editingMatrix.id));
    }
    onCancel();
  }, [dispatch, onCancel, matrix, editingMatrix]);

  const onCancelEdit = useCallback(() => {
    // Use original matrix on cancel
    dispatch(finishEdit(matrix));
    onCancel();
  }, [dispatch, onCancel, matrix]);

  const editingMatrixRef = useRef(editingMatrix);
  useEffect(() => {
    editingMatrixRef.current = editingMatrix;
  }, [editingMatrix]);

  useEffect(() => {
    return () => {
      if (editingMatrixRef.current) {
        dispatch(finishEdit(editingMatrixRef.current));
      }
    };
  }, [dispatch]);

  return (
    <div className="ConnectionsAdvanced">
      <div className="ConnectionsAdvanced-main">
        <div className="ConnectionsAdvanced-top-container">
          <div className="intro-paragraph">
            Add, remove, and edit connections between nodes in the table. Run an
            uncertainty analysis to test out how connections impact the entire
            ecosystem.
          </div>
          <div className="ConnectionsAdvanced-button-container">
            <div className="ConnectionsAdvanced-actions">
              <ButtonNew
                buttonClasses="text-button"
                label="Export"
                iconLeft="exportIcon"
                onClick={handleExport}
              />
              <ImportConnections matrix={matrix} />
            </div>
          </div>
        </div>

        <div className="ConnectionsAdvanced-descriptor-container">
          {descriptor}
        </div>

        <div className="ConnectionsAdvanced-content">
          {editingMatrix ? (
            <ConnectionsTable
              matrix={editingMatrix}
              setDescriptor={setDescriptor}
            />
          ) : null}
        </div>
      </div>

      <div className="ConnectionsAdvanced-footer">
        <div className="ConnectionsAdvanced-footer-buttons">
          <ButtonNew
            buttonClasses="secondary-button-small"
            label="Cancel"
            onClick={onCancelEdit}
          />
          <ButtonNew
            buttonClasses="primary-button-small"
            label="Ok"
            onClick={onConfirmEdit}
          />
        </div>
      </div>
    </div>
  );
};

export default ConnectionsAdvanced;
